import React from 'react';
import './StupidSystemDesigns.css';

const uberDesignCode = String.raw`
#include <iostream>
#include <string>
#include <unordered_set>
#include <unordered_map>
#include <vector>
#include <cmath>
#include <climits>

using namespace std;

enum UserState { FREE, BOOKED }; 
enum TripState { OPEN, IN_PRG, COMPLETED }; 

class Customer {
    int custId;
    string custName;
    unordered_set<int> trips;
    UserState state;
    
public:
    Customer(int id, string name) {
        custId = id;
        custName = name;
        trips = {};
        state = UserState::FREE;
    }
    
    UserState getState() { return state; }
    string getName() { return custName; }
    
    void addTrip(int tripId) {
        state = UserState::BOOKED;
        trips.insert(tripId);
    }
    
    void finishTrip() {
        state = UserState::FREE;
    }
};

class Driver {
    int driverId;
    string driverName;
    pair<int,int> location;
    UserState state;
    unordered_set<int> trips;
    
public:
    Driver(int id, string name, int lat, int ln) {
        driverId = id;
        driverName = name;
        location = {lat, ln};  
        state = UserState::FREE;
    }
    
    pair<int,int> getLocation() { return location; }
    UserState getState() { return state; }
    string getName() { return driverName; }
    
    void addTrip(int tripId) {
        state = UserState::BOOKED;
        trips.insert(tripId);
    }
    
    void finishTrip(pair<int,int> dropLocation) {
        state = UserState::FREE;
        location = dropLocation;
    }
};

class Trip {
    int tripId;
    int custId;
    int driverId;
    pair<int,int> pickup;
    pair<int,int> drop;
    int fare;
    TripState state;

public:
    Trip(int tId, int cId, pair<int,int> start, pair<int,int> end) {
        tripId = tId;
        custId = cId;
        driverId = -1;
        pickup = start;
        drop = end;
        fare = 5 * getDist(start, end);
        state = TripState::OPEN;
    }
    
    int getDist(pair<int,int> start, pair<int,int> end) {
        int x = (start.first - end.first) * (start.first - end.first);
        int y = (start.second - end.second) * (start.second - end.second);
        return sqrt(x + y);
    }
    
    pair<int,int> getStart() { return pickup; }
    pair<int,int> getDrop() { return drop; }
    int getCustId() { return custId; }
    int getDriverId() { return driverId; }
    
    void setDriver(int dId) { driverId = dId; }
    void setState(TripState s) { state = s; }
};

class GlobalController {
    int userCounter, driverCounter, tripCounter;
    unordered_map<int, Customer*> customerMap;
    unordered_map<int, Driver*> driverMap;
    unordered_map<int, Trip*> tripMap;
    
public:
    GlobalController() {
        userCounter = 1; driverCounter = 1; tripCounter = 1;
    }
    
    void addCustomer(string name) {
        int custId = userCounter++;
        customerMap[custId] = new Customer(custId, name);
        cout << "[SYSTEM] Added Customer: " << name << " (ID: " << custId << ")\n";
    }
    
    void addDriver(string name, int lat, int ln) {
        int driverId = driverCounter++;
        driverMap[driverId] = new Driver(driverId, name, lat, ln);
        cout << "[SYSTEM] Added Driver: " << name << " (ID: " << driverId << ") at [" << lat << "," << ln << "]\n";
    }
    
    int bookTrip(int custId, pair<int,int> start, pair<int,int> end) {
        if (customerMap.find(custId) == customerMap.end()) return -1;
        
        if (customerMap[custId]->getState() == UserState::BOOKED) {
            cout << "[TRIP] Customer " << custId << " is already in a trip. Booking failed.\n";
            return -1;
        }
        
        int tripId = tripCounter++;
        Trip *trip = new Trip(tripId, custId, start, end);
        tripMap[tripId] = trip;
        customerMap[custId]->addTrip(tripId);
        
        cout << "[TRIP] Trip " << tripId << " booked by " << customerMap[custId]->getName() << ".\n";
        return tripId;
    }
    
    int getDist(pair<int,int> start, pair<int,int> end) {
        int x = (start.first - end.first) * (start.first - end.first);
        int y = (start.second - end.second) * (start.second - end.second);
        return sqrt(x + y);
    }
    
    bool assignDriver(int tripId) {
        if (tripMap.find(tripId) == tripMap.end()) return false;
        
        int bestDriver = -1, bestDistance = INT_MAX;
        
        for (int i = 1; i < driverCounter; i++) {
            if (driverMap[i]->getState() == UserState::FREE) {
                int dist = getDist(driverMap[i]->getLocation(), tripMap[tripId]->getStart());
                if (dist < bestDistance) {
                    bestDistance = dist;
                    bestDriver = i;
                }
            }
        }
        
        if (bestDriver == -1) {
            cout << "[ASSIGN] No free drivers available for Trip " << tripId << ".\n";
            return false; 
        }
        
        driverMap[bestDriver]->addTrip(tripId);
        tripMap[tripId]->setDriver(bestDriver);
        tripMap[tripId]->setState(TripState::IN_PRG);
        
        cout << "[ASSIGN] Driver " << driverMap[bestDriver]->getName() 
             << " assigned to Trip " << tripId << " (Distance to pickup: " << bestDistance << ")\n";
        return true;
    }
    
    void completeTrip(int tripId) {
        if (tripMap.find(tripId) == tripMap.end()) return;
        
        Trip* trip = tripMap[tripId];
        trip->setState(TripState::COMPLETED);
        
        int cId = trip->getCustId();
        int dId = trip->getDriverId();
        
        customerMap[cId]->finishTrip();
        driverMap[dId]->finishTrip(trip->getDrop());
        
        cout << "[COMPLETE] Trip " << tripId << " finished. " 
             << driverMap[dId]->getName() << " is now FREE at [" 
             << trip->getDrop().first << "," << trip->getDrop().second << "].\n";
    }
};


int main() {
    GlobalController gc;
    
    cout << "--- TICK 1: INITIALIZATION ---\n";
    gc.addCustomer("Alice"); // ID 1
    gc.addCustomer("Bob");   // ID 2
    gc.addDriver("John", 0, 0);   // ID 1
    gc.addDriver("Sarah", 10, 10); // ID 2
    cout << "\n";
    
    cout << "--- TICK 2: ALICE BOOKS A RIDE ---\n";
    // Alice is at (1,1), wants to go to (5,5). John is at (0,0), Sarah at (10,10). 
    // John should be assigned because he is closer.
    int trip1 = gc.bookTrip(1, {1, 1}, {5, 5});
    gc.assignDriver(trip1);
    cout << "\n";
    
    cout << "--- TICK 3: ALICE TRIES DOUBLE BOOKING & BOB BOOKS ---\n";
    gc.bookTrip(1, {5, 5}, {6, 6}); // Should fail, Alice is booked
    
    // Bob is at (9,9). Sarah is at (10,10) and free. Sarah should get it.
    int trip2 = gc.bookTrip(2, {9, 9}, {20, 20});
    gc.assignDriver(trip2);
    cout << "\n";
    
    cout << "--- TICK 4: ALICE'S TRIP ENDS ---\n";
    gc.completeTrip(trip1);
    cout << "\n";
    
    cout << "--- TICK 5: ALICE BOOKS AGAIN ---\n";
    // Alice is now at (5,5) wanting to go to (8,8).
    // John is free and currently at (5,5). Sarah is busy.
    int trip3 = gc.bookTrip(1, {5, 5}, {8, 8});
    gc.assignDriver(trip3);

    return 0;
}

`;

function UberDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Uber Design</h1>
                            <p className="system-designs-subtitle">
                                Base Low level design for a ride-booking system that matches free drivers with customers and keeps trip state transitions valid.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Register customers and drivers with an initial <code>FREE</code> state and location.</li>
                                <li>Allow customers to request a ride from a pickup location to a destination.</li>
                                <li>Match an available <code>FREE</code> driver to an open trip.</li>
                                <li>Reject invalid actions, including double-booking an active customer.</li>
                                <li>Complete trips, calculate the final fare, update the driver's location, and release both users.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Requirements Satisfied</h2>
                            <ul>
                                <li><strong>Onboarding:</strong> Customers and drivers begin with known IDs, locations, and <code>FREE</code> states.</li>
                                <li><strong>Ride Booking:</strong> A customer creates an <code>OPEN</code> trip with pickup and destination locations.</li>
                                <li><strong>Driver Assignment:</strong> The system selects a <code>FREE</code> driver and moves both driver and customer to <code>BOOKED</code>.</li>
                                <li><strong>State Management:</strong> Only <code>FREE</code> -&gt; <code>BOOKED</code> -&gt; <code>FREE</code> is valid for users, and only <code>OPEN</code> -&gt; <code>IN_PRG</code> -&gt; <code>COMPLETED</code> is valid for trips.</li>
                                <li><strong>Trip Completion:</strong> Completion calculates the fare, moves the driver to the drop-off point, and releases both users.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Logic Used</h2>
                            <ul>
                                <li><strong>Explicit state machines:</strong> User and trip transitions are validated before any state is mutated.</li>
                                <li><strong>Availability matching:</strong> Assignment searches only drivers in the <code>FREE</code> state.</li>
                                <li><strong>Transactional assignment:</strong> A driver and customer are updated together so an open trip cannot be partially booked.</li>
                                <li><strong>Completion bookkeeping:</strong> Fare calculation and location updates happen before returning the customer and driver to <code>FREE</code>.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>C++ Sample Code</h2>
                            </div>
                            <pre className="design-code-block"><code>{uberDesignCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        The surge pricing is probably somewhere nearby, waiting for its own design page.
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default UberDesign;